import { WORKERS } from '../../engine/data';
import type { Worker } from '../../engine/data';
import type { Tone } from '../../ui';

export interface Pt {
  x: number;
  y: number;
}

/** Least-squares line; null when x has (almost) no spread. */
export function fitLine(pts: Pt[]): { slope: number; intercept: number } | null {
  const n = pts.length;
  const sX = pts.reduce((a, p) => a + p.x, 0);
  const sY = pts.reduce((a, p) => a + p.y, 0);
  const sXY = pts.reduce((a, p) => a + p.x * p.y, 0);
  const sX2 = pts.reduce((a, p) => a + p.x * p.x, 0);
  const denom = n * sX2 - sX * sX;
  if (n < 2 || Math.abs(denom) <= 0.01) return null;
  const slope = (n * sXY - sX * sY) / denom;
  return { slope, intercept: (sY - slope * sX) / n };
}

export const clamp100 = (v: number) => Math.max(0, Math.min(100, v));

export interface OtResilience {
  hasOT: boolean;
  first: string;
  workerRate: number;
  avgPeerRate: number;
  resilScore: number | null;
  label: string;
  tone: Tone;
  desc: string;
  peers: { init: string; otHrs: number; fed: number }[];
  maxOT: number;
  /** Peer trend segment (clamped to 0-100), if computable. */
  trend: { x1: number; y1: number; x2: number; y2: number } | null;
  personal: { wk: number; hrs: number; fed: number }[];
  pSlope: number;
  pIntercept: number;
  slopeLabel: string;
  slopeTone: Tone;
  minH: number;
  maxH: number;
}

export function computeOtResilience(w: Worker): OtResilience {
  const first = w.name.split(' ')[0];
  const peers = Object.values(WORKERS).map((p) => ({ init: p.init, otHrs: p.otHrs || 0, fed: p.fed }));
  const withOT = peers.filter((p) => p.otHrs > 0);
  const hasOT = w.otHrs > 0;

  // FED per OT hour: higher = less resilient.
  const workerRate = hasOT ? w.fed / w.otHrs : 0;
  const peerRates = withOT.filter((p) => p.init !== w.init).map((p) => p.fed / p.otHrs);
  const avgPeerRate = peerRates.length ? peerRates.reduce((a, b) => a + b, 0) / peerRates.length : 0;
  const maxRate = Math.max(...withOT.map((p) => p.fed / p.otHrs), workerRate || 0, 0.01);
  const resilScore = hasOT ? Math.round(Math.max(0, 100 - (workerRate / maxRate) * 100)) : null;

  const s = resilScore ?? 0;
  const label = !hasOT
    ? 'No OT this period'
    : s >= 65
      ? 'High Resilience'
      : s >= 35
        ? 'Moderate Resilience'
        : 'Low Resilience';
  const tone: Tone = !hasOT ? 'muted' : s >= 65 ? 'good' : s >= 35 ? 'warn' : 'bad';
  const desc = !hasOT
    ? `${first} has not worked overtime this week — no OT–FED relationship to measure.`
    : s >= 65
      ? `${first}'s FED rises only ${workerRate.toFixed(1)} points per OT hour — well below the team average of ${avgPeerRate.toFixed(1)}. They absorb additional hours with minimal biometric impact.`
      : s >= 35
        ? `${first}'s FED rises ~${workerRate.toFixed(1)} points per OT hour, close to the team average of ${avgPeerRate.toFixed(1)}. Moderate susceptibility — monitor carefully if further OT is planned.`
        : `${first}'s FED rises ${workerRate.toFixed(1)} points per OT hour — significantly above the team average of ${avgPeerRate.toFixed(1)}. High susceptibility: additional hours carry disproportionate fatigue risk.`;

  const maxOT = Math.max(...peers.map((p) => p.otHrs), 1);
  let trend: OtResilience['trend'] = null;
  const tf = fitLine(withOT.map((p) => ({ x: p.otHrs, y: p.fed })));
  if (tf) {
    trend = {
      x1: 0,
      y1: clamp100(tf.intercept),
      x2: maxOT,
      y2: clamp100(tf.slope * maxOT + tf.intercept),
    };
  }

  // Synthesised 12 weeks of (hours, FED): hours scale with FED relative to the current week.
  const normalWeekH = w.normalHrs || 40;
  const personal = (w.fed12 || []).map((fedVal, i) => {
    const ratio = w.fed > 0 ? fedVal / w.fed : 1;
    const hrs = Math.max(normalWeekH * 0.6, Math.min(normalWeekH * 1.6, (w.shiftHrs || normalWeekH) * ratio));
    return { wk: i + 1, hrs: Math.round(hrs * 2) / 2, fed: fedVal };
  });
  const pf = fitLine(personal.map((p) => ({ x: p.hrs, y: p.fed })));
  const pSlope = pf ? pf.slope : 0;
  const pIntercept = pf ? pf.intercept : personal.reduce((a, p) => a + p.fed, 0) / (personal.length || 1);
  const slopeLabel =
    pSlope > 3
      ? 'Steep — high sensitivity to hours'
      : pSlope > 1.5
        ? 'Moderate sensitivity to hours'
        : 'Low sensitivity to hours';
  const slopeTone: Tone = pSlope > 3 ? 'bad' : pSlope > 1.5 ? 'warn' : 'good';
  const hrsList = personal.map((p) => p.hrs);

  return {
    hasOT,
    first,
    workerRate,
    avgPeerRate,
    resilScore,
    label,
    tone,
    desc,
    peers,
    maxOT,
    trend,
    personal,
    pSlope,
    pIntercept,
    slopeLabel,
    slopeTone,
    minH: (hrsList.length ? Math.min(...hrsList) : 0) - 4,
    maxH: (hrsList.length ? Math.max(...hrsList) : 0) + 4,
  };
}
